import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Divider,
  InputAdornment,
  Popover,
  TextField,
  Typography,
} from "@mui/material";
import AddCircleOutlineOutlined from "@mui/icons-material/AddCircleOutlineOutlined";
import CheckCircle from "@mui/icons-material/CheckCircle";
import RadioButtonUnchecked from "@mui/icons-material/RadioButtonUnchecked";
import Search from "@mui/icons-material/Search";

interface MultiSelectFilterProps {
  label: string;
  options: string[];
  selected: string[];
  onChange: (selected: string[]) => void;
  counts?: Record<string, number>;
}

const MultiSelectFilter: React.FC<MultiSelectFilterProps> = ({
  label,
  options,
  selected,
  onChange,
  counts,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>("");

  const open = Boolean(anchorEl);
  const hasSelection = selected.length > 0;

  const handleClose = () => {
    setAnchorEl(null);
    setSearchTerm("");
  };

  const toggleOption = (option: string) => {
    if (selected.includes(option)) {
      onChange(selected.filter((value) => value !== option));
    } else {
      onChange([...selected, option]);
    }
  };

  const filteredOptions = useMemo(
    () => options.filter((option) => option.toLowerCase().includes(searchTerm.toLowerCase())),
    [options, searchTerm]
  );

  const summaryLabel = selected.length === 1 ? selected[0] : `${selected.length} selected`;

  return (
    <>
      <Button
        onClick={(event) => setAnchorEl(event.currentTarget)}
        variant="outlined"
        startIcon={<AddCircleOutlineOutlined fontSize="small" />}
        sx={{
          textTransform: "none",
          borderStyle: "dashed",
          borderRadius: "10px",
          height: 40,
          px: 2,
          color: hasSelection ? "primary.main" : "text.secondary",
          borderColor: hasSelection ? "primary.main" : "rgba(0, 0, 0, 0.23)",
          "&:hover": {
            borderStyle: "dashed",
            borderColor: hasSelection ? "primary.main" : "rgba(0, 0, 0, 0.23)",
          },
        }}
      >
        {label}
        {hasSelection && (
          <>
            <Divider
              orientation="vertical"
              flexItem
              sx={{ mx: 1.5, my: 0.25, borderColor: "primary.main", opacity: 0.4 }}
            />
            <Typography
              component="span"
              sx={{ fontWeight: 600, fontSize: 14, color: "primary.main" }}
            >
              {summaryLabel}
            </Typography>
          </>
        )}
      </Button>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Box sx={{ width: "fit-content", minWidth: 250, maxWidth: 300 }}>
          <Box sx={{ p: 1.5 }}>
            <TextField
              autoFocus
              size="small"
              fullWidth
              placeholder={label}
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" sx={{ color: "text.secondary" }} />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          <Box sx={{ maxHeight: 280, overflowY: "auto" }}>
            {filteredOptions.length === 0 ? (
              <Typography sx={{ px: 2, py: 1.5, color: "text.secondary", fontSize: 14 }}>
                No options found
              </Typography>
            ) : (
              filteredOptions.map((option) => {
                const isSelected = selected.includes(option);
                return (
                  <Box
                    key={option}
                    onClick={() => toggleOption(option)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      px: 2,
                      py: 1,
                      cursor: "pointer",
                      "&:hover": { backgroundColor: "action.hover" },
                    }}
                  >
                    {isSelected ? (
                      <CheckCircle fontSize="small" sx={{ color: "primary.main" }} />
                    ) : (
                      <RadioButtonUnchecked fontSize="small" sx={{ color: "text.disabled" }} />
                    )}
                    <Typography sx={{ fontSize: 14, flexGrow: 1 }}>{option}</Typography>
                    {counts && counts[option] > 0 && (
                      <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
                        {counts[option] ?? 0}
                      </Typography>
                    )}
                  </Box>
                );
              })
            )}
          </Box>
          <Divider />
          <Button
            fullWidth
            disabled={!hasSelection}
            onClick={() => onChange([])}
            sx={{ textTransform: "none", color: "text.secondary", py: 1 }}
          >
            {`Clear ${label}`}
          </Button>
        </Box>
      </Popover>
    </>
  );
};

export default MultiSelectFilter;
